import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BaseService} from "../../services/base.service";
import {HttpClient} from "@angular/common/http";
import {Meta, Title} from "@angular/platform-browser";
import {ActivatedRoute} from "@angular/router";
import {finalize, takeUntil} from "rxjs/operators";
import {Subject, Subscription} from "rxjs";
import * as moment from "moment/moment";
import {formatUnixDateKyiv} from "../../shared/kyiv-date";
import {removeDuplicateBrand} from "../../shared/brand-name";
import {OrdersHistory, OrdersInfo, Returns} from "../../interfaces/orders-history";
import {Delivery} from "../../interfaces/delivery";
import {ReturnGoods} from "../../interfaces/return-goods";
import {Currencies} from "../../interfaces/currencies";
import {OrdersHistoryData, UsersAddresses} from "../../interfaces/orders-history-data";
import {ModalOrderReturnComponent} from "./modal-order-return/modal-order-return.component";
import {ModalTrackStatusComponent} from "./modal-track-status/modal-track-status.component";
import {ModalTtnStatusShowComponent} from "./modal-ttn-status-show/modal-ttn-status-show.component";
import {CheckTtnNp} from "../../interfaces/check-ttn-np";
import {ModalChangeDeliveryDateComponent} from "./modal-change-delivery-date/modal-change-delivery-date.component";
import {OrganizationsService} from "../../services/organizations.service";
import {Organization} from "../../interfaces/organization";
import {BuCalculationItem, BuCalculationsDetailed} from "../../interfaces/bu-accounts";
import {RequestData} from "../../interfaces/request-data";
import {HttpErrorNotifyService} from "../../services/http-error-notify.service";
import {AssistantActionsService} from "../../services/assistant-actions.service";
import {AssistantHighlight} from "../../shared/assistant-highlight";

/**
 * Що просить показати помічник (#965, #966): підсвітити замовлення, одне розкрити, для одного відкрити вікно статусу (track);
 * from..to - дати їхніх рядків в історії
 */
interface AssistantOrdersShow {
  ids: number[];
  open: number | null;
  track: number | null;
  from: string;
  to: string;
  // Період уже розширено під ці замовлення - вдруге список не перечитуємо
  widened: boolean;
}

@Component({
  selector: 'app-orders-history-new',
  templateUrl: './orders-history-new.component.html',
  styleUrls: ['./orders-history-new.component.scss']
})
export class OrdersHistoryNewComponent extends BaseService implements OnDestroy, OnInit {
  @ViewChild(ModalOrderReturnComponent) modalOrderReturnComponentRef: ModalOrderReturnComponent;
  @ViewChild(ModalTrackStatusComponent) modalTrackStatusComponentRef: ModalTrackStatusComponent;
  @ViewChild(ModalTtnStatusShowComponent) modalTtnStatusShowComponentRef: ModalTtnStatusShowComponent;
  @ViewChild(ModalChangeDeliveryDateComponent) modalChangeDeliveryDateComponentRef: ModalChangeDeliveryDateComponent;

  private http = inject(HttpClient);
  private titleService = inject(Title);
  private meta = inject(Meta);
  private activatedRoute = inject(ActivatedRoute);
  private organizationsService = inject(OrganizationsService);
  private notify = inject(HttpErrorNotifyService);
  private assistantActions = inject(AssistantActionsService);

  // Картка #965: замовлення, на які вказав помічник; що він просить показати, поки список не готовий (assistantShow);
  // номер запиту списку - розкриття й прокрутка чекають відповіді на останній
  public highlight = new AssistantHighlight(inject(ElementRef));
  private assistantShow: AssistantOrdersShow | null = null;
  private dataSeq = 0;
  private dataLoading = false;
  private assistantUnregister: Array<() => void> = [];

  public isDestroyed: Subject<boolean> = new Subject();
  private isTransferringBalance: boolean = false;
  public isDarkBackground = false;
  public isModalInfoShow = false;
  public dataRouteSubject;
  public date_from = moment().subtract(180, 'days').format('YYYY-MM-DD');
  public date_to = moment().add(30, 'days').format('YYYY-MM-DD');
  public delivery = '';
  public search = '';
  public deliveryInfo: Delivery[];
  public currencies: Currencies[];
  public orders: OrdersInfo[];
  public nalojkiSum;
  public sum;
  public timer;
  // Попередній запит списку скасовуємо: інакше старша відповідь пошуку, що прийшла пізніше, перезаписувала новішу
  private dataSub: Subscription;
  public returns: Returns[];
  public returnSelected: Returns;
  public returnGoods: ReturnGoods[];
  public startPrint = false;
  public pageType = 1; // 1 - predoplata, 2 - nalojki
  public sumFormatted;
  public orderPrint: OrdersInfo;
  public printOrder = false;
  public currentYear: number;
  public exchangeRatesEUR;
  public selectedOrder: OrdersInfo;
  public activeOrganization: Organization;
  public buOrders: BuCalculationItem[] = [];
  public buBalance: number = 0;

  constructor(
  ) {
    super();

    const title = 'Маслоточка | Замовлення';
    this.titleService.setTitle(title);
    this.currentYear = moment().year();
    this.meta.addTag({name: 'title', content: title});
    this.meta.addTag({name: 'og:title', content: title});
    this.meta.addTag({name: 'twitter:title', content: title});
    this.meta.addTag({name: 'DC.title', content: title});
  }

  ngOnInit() {
    this.dataRouteSubject = this.activatedRoute.data.subscribe(data => {
      this.pageType = data['type'];
    });

    this.organizationsService.organizations$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((organizations: Organization[]) => {
        if (!organizations || !organizations.length) {
          return;
        }

        const active = organizations.find(org => org.is_active === '1' || org.is_active === true);
        const sameActive = !!active && !!this.activeOrganization &&
          String(active.id) === String(this.activeOrganization.id) && active.type === this.activeOrganization.type;

        this.activeOrganization = active;

        // Шапка є на кожній сторінці й при переході перечитує організації: той самий список приходить удруге (з кешу
        // сервісу і після її запиту). Активна організація та сама - список не перечитуємо, інакше друга відповідь
        // згортала замовлення, щойно розкрите помічником чи клієнтом (#966)
        if (!sameActive) {
          this.getData();
        }
      });

    this.assistantUnregister.push(
      this.assistantActions.register('open_order', (action) => {
        const id = Number(action.order_id);

        // expand: false - лише прокрутити й підсвітити (#966); без поля - розкрити, як раніше
        this.showForAssistant([id], action.expand === false ? null : id, action.date, action.date);
      }),
      this.assistantActions.register('highlight', (action) => {
        const ids = Array.isArray(action.ids) ? action.ids.map(Number) : [];

        this.showForAssistant(ids, null, action.date_from, action.date_to);
      }),
      // Вікно статусу замовлення (#966) - як клік по статусу в рядку
      this.assistantActions.register('show_track_status', (action) => {
        const id = Number(action.order_id);

        this.showForAssistant([id], null, action.date, action.date, id);
      }),
    );
  }

  getData() {
    // Новий запит робить попередній застарілим: його завершення вже не розкриває замовлення помічника
    this.dataSeq++;
    this.dataLoading = true;

    if (this.dataSub) {
      this.dataSub.unsubscribe();
    }

    if (this.activeOrganization && this.activeOrganization.type !== 'fiz') {
      this.getBuData();
      return;
    }

    const seq = this.dataSeq;

    this.dataSub = this.http.post(this.apiRoot + 'orders_history/data', {
      date_from: this.date_from,
      date_to: this.date_to,
      delivery: this.delivery,
      search: this.search,
      type: this.pageType,
    })
      .pipe(takeUntil(this.isDestroyed), finalize(() => this.onListLoaded(seq)))
      .subscribe((data: OrdersHistory) => {
        this.returns = data.returns;
        this.orders = data.orders;

        this.deliveryInfo = Object.values(data.delivery_info)
          .sort((a, b) => {
            const sortA = parseInt(a.sort);
            const sortB = parseInt(b.sort);

            if (sortA < sortB) {
              return -1;
            }
            if (sortA > sortB) {
              return 1;
            }

            return 0;
          });

        this.sum = data.summa;

        if (this.sum.summa === null) {
          return;
        }

        this.currencies = Object.values(data.currencies);

        this.exchangeRatesEUR = this.currencies.find(element => element.id == 3).rate;

        if (this.orders) {
          this.orders = data.orders.map(el => {
            el.datetime = moment(el.datetime).format('DD.MM.YYYY HH:mm:ss');
            el.datedelivery = moment(el.datedelivery).format('DD.MM.YYYY');

            if (el.datedelivery === 'Invalid date') {
              el.datedelivery = '';
            }

            if (el.delivery_hour && el.delivery_hour !== '00:00') {
              el.deliveryHours = String(el.delivery_hour);
            } else {
              el.deliveryHours = '';
            }

            el.summa_converted = (el.summa < 0 ? el.summa * this.exchangeRatesEUR * 100 / 100 * -1 : el.summa * this.exchangeRatesEUR * 100 / 100);

            el.summa_converted_format = (el.summa_converted).toFixed(2);

            el.summa_converted_format_mobile = Math.ceil(el.summa_converted);

            el.declaraciya_text = el.declaraciya;

            el.added_ttn = false;

            el.show_order = false;

            if (el.delivery_id) {
              const del = this.deliveryInfo.find((del) => del.id === el.delivery_id);

              if (del && Array.isArray(del.delivery_dates)) {
                el.changeDeliveryDate = del.delivery_dates.map(date => formatUnixDateKyiv(date));

                if (Array.isArray(del.delivery_hours)) {
                  el.changeDeliveryDatesHours = {};
                } else {
                  el.changeDeliveryDatesHours = del.delivery_hours;
                }
              }
            }

            return el;
          });
        }

        if (this.delivery) {
          this.delivery = this.deliveryInfo.find(el => String(el.id) == this.delivery).id || '';
        }

        if (this.sum.summa < 0) {
          this.sumFormatted = ((this.sum.summa * this.exchangeRatesEUR * 100) / -100).toFixed(2);
        } else {
          this.sumFormatted = ((this.sum.summa * this.exchangeRatesEUR * 100) / 100).toFixed(2);
        }

        if (data.nalojki_sum && data.nalojki_sum.orders_sum) {
          data.nalojki_sum.orders_profit = (data.nalojki_sum.orders_sum - this.sum.summa).toFixed(2);

          data.nalojki_sum.orders_sum = (data.nalojki_sum.orders_sum).toFixed(2);

          this.nalojkiSum = data.nalojki_sum;
        }

        if (data.returns) {
          this.returns = data.returns.map(ret => {
            ret.date = moment(ret.datetime).format('DD.MM.YYYY');
            ret.datetime = moment(ret.datetime).format('DD.MM.YYYY HH:mm:ss');
            ret.sum_fixed = ret.summa.toFixed(2);
            ret.sum_fixed_uan = (Math.round((ret.summa * ret.rate * 100)) / 100).toFixed(2);
            ret.sum_fixed_uan_mobile = (Math.ceil((ret.summa * ret.rate * 100)) / 100).toFixed(0);
            return ret;
          });
        }
      });
  }

  getBuData() {
    const seq = this.dataSeq;

    this.dataSub = this.http.get<BuCalculationsDetailed>(this.apiRoot + 'bu_accounts/calculations', {
      params: { detailed: '1' }
    })
      .pipe(takeUntil(this.isDestroyed), finalize(() => this.onListLoaded(seq)))
      .subscribe(data => {
        if (data.success) {
          this.buBalance = data.balance;
          this.buOrders = data.data || [];

          for (let buOrder of this.buOrders) {
            buOrder.datetime = moment(buOrder.datetime).format('DD.MM.YYYY HH:mm:ss');
          }
        }
      });
  }

  clearFilters() {
    this.date_from = moment().subtract(180, 'days').format('YYYY-MM-DD');
    this.date_to = moment().add(30, 'days').format('YYYY-MM-DD');
    this.delivery = '';
    this.search = '';

    this.getData();
  }

  searchData() {
    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      this.getData();
    }, 500);
  }

  crossClears() {
    clearTimeout(this.timer);
    this.search = '';
    this.getData();
  }

  downloadFile() {
    const date_from = this.date_from;
    const date_to = this.date_to;
    const pageType = this.pageType;

    window.open(this.apiRoot + `orders_history/revise/?date_from=${date_from}&date_to=${date_to}&type=${pageType}`, "_blank");
  }

  setTtn(order: OrdersInfo) {
    if (order.declaraciya_text) {
      order.declaraciya_text = order.declaraciya_text.replace(/<\/?[^>]+(>|$)/g, "");
      order.declaraciya_text = order.declaraciya_text.replace(/[^0-9]/g, '');

      if (order.declaraciya_text && !isNaN(Number(order.declaraciya_text))) {
        this.http.post('https://api.novaposhta.ua/v2.0/json/', {
          modelName: 'TrackingDocument',
          calledMethod: 'getStatusDocuments',
          methodProperties: {
            Documents: [
              {
                DocumentNumber: order.declaraciya_text
              }
            ]
          }
        })
          .pipe(takeUntil(this.isDestroyed))
          .subscribe((data: CheckTtnNp) => {
            // На неіснуючий номер НП відповідає success:true, а в документі - StatusCode 3 («Номер не знайдено»)
            const ttnDocument = data && data.success && Array.isArray(data.data) ? data.data[0] : null;

            if (ttnDocument && String(ttnDocument.StatusCode) !== '3') {
              this.http.post(this.apiRoot + 'orders_history/set_ttn_number', {
                order_id: order.order_id,
                ttn: order.declaraciya_text
              })
                .pipe(takeUntil(this.isDestroyed))
                .subscribe((resp: RequestData) => {
                  // Бек сам перевіряє, чи можна міняти ТТН (#537), і відмовляє зі статусом 200 -
                  // раніше рядок однаково підсвічувався як збережений
                  if (resp && resp.error) {
                    this.notify.show(
                      'Номер ТТН не збережено',
                      'Для цього замовлення номер ТТН змінити вже не можна. Оновіть сторінку.'
                    );
                    return;
                  }

                  order.added_ttn = true;
                });
            } else {
              this.incorrectTtnModalShow();
            }
          });
      }
    }
  }

  incorrectTtnModalShow() {
    this.modalTtnStatusShowComponentRef.ttnModalShow();
  }

  deletePreReturn(return_id: number) {
    const message = 'Ви впевнені, що бажаєте відмінити заявку на повернення?' +
      '\nМожливість повторно зробити заявку на повернення по одній позиції в замовленні відсутня!';

    if (confirm(message)) {
      this.http.post(this.apiRoot + 'orders_history/delete_return_draft', {
        return_id
      })
        .pipe(takeUntil(this.isDestroyed))
        .subscribe(() => {
          this.getData();
        });
    }
  }

  showModalReturns(returned: Returns) {
    this.modalOrderReturnComponentRef.showModalOrderReturns(returned);
  }

  setPrintChange() {
    this.startPrint = true;
  }

  transferBalance() {
    // повторний клік, поки перенесення у роботі, переносив би баланс удруге
    if (this.isTransferringBalance) {
      return;
    }

    const message = `Ви впевнені, що потрібно перевести ${this.sum.summa < 0 ? 'баланс' : 'борг'} з накладених платежів на передоплату?`;

    if (confirm(message)) {
      this.isTransferringBalance = true;

      // Тільки POST з явним підтвердженням: по GET-посиланню бек перенесення більше не робить (#249, #400)
      this.http.post(this.apiRoot + 'orders_history/transfer_balance', {confirm: true})
        .pipe(
          takeUntil(this.isDestroyed),
          finalize(() => this.isTransferringBalance = false)
        )
        .subscribe(() => {
          this.closeModal();
          this.getData();
        });
    }
  }

  downloadRnPdf(event: any, id: number) {
    event.stopPropagation();

    window.open(this.apiRoot + `orders_history/download_rn_pdf/?order_id=${id}`, "_blank");
  }

  showOrder(order: OrdersInfo) {
    // Згортання: склад замовлення вже завантажено при розгортанні, перезапит не потрібен
    if (order.show_order) {
      order.show_order = false;
      return;
    }

    this.http.get(this.apiRoot + 'orders_history/get_order_transfer', {
      params: {
        order_id: order.order_id
      }
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((data: OrdersHistoryData) => {
        // Адресу не знайдено - бек (crud->read) віддає 0 замість об'єкта; запис .phone у число кидав TypeError
        // і замовлення не розкривалось
        const usersAddresses = data.data.users_addresses || {} as UsersAddresses;
        const orderedProducts = data.data.ordered_products;
        const returns = data.data.returns;
        const returnStatuses = Object.values(data.data.return_statuses);

        if (data.data.prro.length) {
          order.prro = data.data.prro;
        }

        const allPack_id = {};

        for (let orderProduct of orderedProducts) {
          orderProduct.name = orderProduct.name.replaceAll('&nbsp;', ' ');
          orderProduct.name = orderProduct.name.replace('|', '(');
          orderProduct.name = orderProduct.name.replace('|', ')');
          // «MANN FILTER MANN-FILTER Фільтр мастил» -> «MANN-FILTER Фільтр мастил» (#855)
          orderProduct.name = removeDuplicateBrand(orderProduct.name, orderProduct.brand);

          if (orderProduct.artikul === null) {
            orderProduct.artikul = '';
          } else if (typeof orderProduct.artikul === "number") {
            orderProduct.firstArticle = orderProduct.artikul;
          } else {
            orderProduct.firstArticle = orderProduct.artikul.split('|||')[0];
            orderProduct.resultLastArticleGood = orderProduct.artikul.split('|||');
            orderProduct.resultLastArticleGood.splice(0, 1);
            orderProduct.lastArticles = undefined;
            if (orderProduct.resultLastArticleGood.length) {
              orderProduct.lastArticles = orderProduct.resultLastArticleGood;
            } else {
              orderProduct.resultLastArticleGood = [];
            }
          }

          if (orderProduct.pack_id === 772 ||
            orderProduct.pack_id === 774 ||
            orderProduct.pack_id === 775 ||
            orderProduct.pack_id === 776 ||
            orderProduct.pack_id === 777 ||
            orderProduct.pack_id === 888 ||
            orderProduct.pack_id === 999) {
            continue;
          }

          allPack_id[String(orderProduct.pack_id)] = 1;
        }

        order.good_ids = Object.keys(allPack_id).join(',');

        usersAddresses.phone = usersAddresses.phone != null && usersAddresses.phone.toString().length === 9 ? '0' + usersAddresses.phone : usersAddresses.phone;

        if (returns && returns.length) {
          for (let returnGoodName of returns) {
            // return_status_id = 0 (NULL у базі) або поза довідником - замовлення не розкривалось через TypeError
            returnGoodName.return_status_name = returnStatuses.find(el => el.id === returnGoodName.return_status_id)?.name || '';
            returnGoodName.datetime = moment(returnGoodName.datetime).format('DD.MM.YYYY HH:mm:ss');

            if (returnGoodName.summa_uah == 0){
              returnGoodName.summa_converted = (returnGoodName.summa < 0 ? returnGoodName.summa * returnGoodName.rate * 100 / 100 * -1 : returnGoodName.summa * returnGoodName.rate * 100 / 100);
            } else {
              returnGoodName.summa_converted = (returnGoodName.summa_uah < 0 ? returnGoodName.summa_uah * -1 : returnGoodName.summa_uah);
            }
            returnGoodName.summa_converted_format = Math.ceil(returnGoodName.summa_converted);
          }

          order.returns = returns;
        }

        order.show_order = true;

        // Сервісні позиції складу не мають, а бек (intval) віддає їм war = 0 замість null -
        // без пропуску будь-яке замовлення з такою позицією втрачало зміну дати.
        const servicePackIds = [772, 774, 775, 776, 777, 888, 999];
        order.canChangeDeliveryDate = !orderedProducts.some(p => !servicePackIds.includes(p.pack_id) && p.war != null && p.war !== 1 && p.war !== 7);

        order.ordered_products = orderedProducts;

        order.users_addresses = usersAddresses;

        order.selectedDate = "0";
      })
  }

  download(id: number) {
    window.open(this.apiRoot + `orders_history/download/?order_id=${id}`, "_blank");
  }

  userPrinterGetOrder(order: OrdersInfo) {
    this.orderPrint = order;
    this.orderPrint.liters = parseFloat(this.orderPrint.liters).toFixed(2);

    this.printOrder = true;
    this.startPrint = true;
    setTimeout(() => {
      window.print();

      this.startPrint = false;
      this.printOrder = false;
    }, 0);
  }

  showTrackStatus(order: OrdersInfo) {
    this.modalTrackStatusComponentRef.modalTrackStatusShow(order);
  }

  showInfo() {
    this.isModalInfoShow = true;
    this.isDarkBackground = true;
  }

  deliveryDateModalShow(order: OrdersInfo) {
    // Дати беруться зі списку доставок (delivery_info). Доставки замовлення в ньому немає (недоступна
    // клієнту зараз) - модалка відкривалась з порожнім списком, а «Змінити» мовчки нічого не робила
    if (!order.changeDeliveryDate || !order.changeDeliveryDate.length) {
      this.notify.show(
        'Дату доставки не змінено',
        'Для цього замовлення зараз немає доступних дат відправки. Щоб змінити дату, зверніться до менеджера.'
      );
      return;
    }

    this.selectedOrder = order;
    this.modalChangeDeliveryDateComponentRef.modalShow();
  }

  onDeliveryDateUpdated() {
    this.getData();
  }

  closeModal() {
    this.isModalInfoShow = false;
    this.isDarkBackground = false;
  }

  /** Список завантажено (чи запит упав): показати те, що просив помічник */
  private onListLoaded(seq: number) {
    if (seq !== this.dataSeq) {
      return;
    }

    this.dataLoading = false;
    this.applyAssistantShow();
  }

  /**
   * Команда помічника: підсвітити замовлення ids, одне з них (open) розкрити, для одного (track) відкрити вікно статусу.
   * from..to - дати їхніх рядків в історії (РРРР-ММ-ДД, від бека). Розкриття (open_order) приходить першим, підсвітка й
   * вікно статусу тієї ж відповіді доповнюють його: підсвічуємо всі, розкриваємо, відкриваємо вікно й період розширюємо під усі.
   */
  private showForAssistant(ids: number[], open: number | null, from: unknown, to: unknown, track: number | null = null) {
    const prev = this.assistantShow;
    const dates = [prev?.from, prev?.to, from, to]
      .filter((date): date is string => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date))
      .sort();

    this.assistantShow = {
      ids: [...new Set([...(prev ? prev.ids : []), ...ids])].filter((id) => Number.isInteger(id) && id > 0),
      open: open ?? prev?.open ?? null,
      track: track ?? prev?.track ?? null,
      from: dates[0] || '',
      to: dates[dates.length - 1] || '',
      widened: false,
    };

    this.highlight.set(this.assistantShow.ids);
    this.applyAssistantShow();
  }

  /**
   * Показати замовлення помічника, коли список готовий: розкрити замовлення, відкрити вікно статусу, підсвітити рядки й
   * прокрутити до першого.
   * Когось із них у списку немає (старші за період чи відсіяні пошуком або доставкою) - один раз розширюємо період до
   * їхніх дат, знімаємо пошук і доставку й перечитуємо список. У білого обліку операції не розкриваються, а список - без
   * періоду: лише підсвічуємо рядки продажів.
   */
  private applyAssistantShow() {
    const show = this.assistantShow;

    if (!show || this.dataLoading || !this.activeOrganization) {
      return;
    }

    if (this.activeOrganization.type === 'fiz' && !show.widened && show.ids.some((id) => !this.saleRow(id))) {
      show.widened = true;

      if (this.widenForAssistant(show)) {
        this.getData();
        return;
      }
    }

    this.assistantShow = null;

    const row = show.open && this.activeOrganization.type === 'fiz' ? this.saleRow(show.open) : undefined;

    if (row && !row.show_order) {
      this.showOrder(row);
    }

    const trackRow = show.track && this.activeOrganization.type === 'fiz' ? this.saleRow(show.track) : undefined;

    if (trackRow) {
      this.showTrackStatus(trackRow);
    }

    this.highlight.ready();
  }

  /** Період - щоб охопив дати замовлень помічника, пошук і доставку - зняти. false - фільтри й так не заважають */
  private widenForAssistant(show: AssistantOrdersShow): boolean {
    let changed = false;

    if (show.from && this.date_from && show.from < this.date_from) {
      this.date_from = show.from;
      changed = true;
    }

    if (show.to && this.date_to && show.to > this.date_to) {
      this.date_to = show.to;
      changed = true;
    }

    if (this.delivery || this.search) {
      this.delivery = '';
      this.search = '';
      changed = true;
    }

    return changed;
  }

  /** Рядок продажу замовлення в списку (не оплата, не повернення, не переміщення) - його розкриваємо й підсвічуємо */
  private saleRow(id: number): OrdersInfo | undefined {
    return (this.orders || []).find(order =>
      Number(order.order_id) === id && !order.is_oplata && !order.is_return && !order.is_transfer
    );
  }

  ngOnDestroy() {
    // Завершення запиту списку (finalize) після знищення вже нічого не розкриває
    this.assistantUnregister.forEach(unregister => unregister());
    this.assistantShow = null;
    this.highlight.destroy();
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
    this.dataRouteSubject.unsubscribe();
  }
}
